import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import { fmtEur } from "./format";

// KpiTile - reuses the same a4-kpi-tile* classes KPIStripBlock/BudgetRiskOverviewBlock's
// tiles already draw from BoardReportPrint.css, so this block's KPI row matches the app's
// existing print styling without introducing new CSS.
function KpiTile({ label, value }) {
  return (
    <div className="a4-kpi-tile">
      <div className="a4-kpi-tile-label">{label}</div>
      <div className="a4-kpi-tile-value">{value}</div>
    </div>
  );
}

KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

// fmtRate renders a whole-number percentage from a 0-1 fraction (e.g. 0.6 -> "60%"). Kept
// local (rather than reusing format.js's signedPct) because a realization rate is never
// signed - it is always a share of committed savings, 0 or positive.
function fmtRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return "—";
  return `${Math.round(Number(rate) * 100)}%`;
}

/**
 * SavingsSummaryKpisBlock - Page 1's savings-by-status KPI tiles (savings_summary_kpis
 * block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/savings-summary ->
 * envelope key "savings_summary" -> boardfigures.SavingsSummary { kpis: { committedTotal,
 * realizedTotal, pipelineTotal, realizationRate, counts: {potential, committed, realized,
 * dismissed} } } (internal/boardfigures/savings.go's BuildSavingsSummary). Money is EUR
 * float (fmtEur direct, no ÷100).
 */
const SavingsSummaryKpisBlock = ({ title = "Savings by status", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/savings-summary`
  );

  const kpis = data?.savings_summary?.kpis ?? null;
  const counts = kpis?.counts ?? null;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-savings-summary-kpis">
      {kpis && (
        <div className="a4-kpi-tiles">
          <KpiTile label="Committed" value={fmtEur(kpis.committedTotal)} />
          <KpiTile label="Realized" value={fmtEur(kpis.realizedTotal)} />
          <KpiTile label="Pipeline" value={fmtEur(kpis.pipelineTotal)} />
          <KpiTile label="Realization rate" value={fmtRate(kpis.realizationRate)} />
        </div>
      )}

      {counts && (
        <div
          className="a4-savings-status-counts"
          style={{ marginTop: "4mm", display: "flex", gap: "6mm", fontSize: "9pt", color: "#6b6b6b" }}
        >
          <span>Potential: {counts.potential}</span>
          <span>Committed: {counts.committed}</span>
          <span>Realized: {counts.realized}</span>
          <span>Dismissed: {counts.dismissed}</span>
        </div>
      )}
    </BaseBlock>
  );
};

SavingsSummaryKpisBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SavingsSummaryKpisBlock;
